import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronRight,
  Sparkles,
  AlertCircle,
  Check,
  RefreshCw,
  Award,
  BookOpen,
  GitBranch,
  Paintbrush,
} from 'lucide-react';
import { EssayScore } from '@/app/score/types/score';
import {
  getCriterionName,
  getCriterionDescription,
  CriterionType,
} from '@/lib/prompts/criterion-prompts';
import { WritingTaskType } from '@/types/writing';

interface ScoreDisplayProps {
  score: EssayScore | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isScoring: boolean;
  scoreError: string | null;
  taskType?: WritingTaskType;
  onRefresh?: () => void;
}

function getBandColor(band: number): string {
  if (band >= 8) return 'var(--chart-1)';
  if (band >= 7) return 'var(--chart-2)';
  if (band >= 6) return 'var(--chart-3)';
  if (band >= 5) return 'var(--chart-4)';
  return 'var(--chart-5)';
}

function getProgressColor(band: number): string {
  if (band >= 8) return 'var(--chart-1)';
  if (band >= 7) return 'var(--chart-2)';
  if (band >= 6) return 'var(--chart-3)';
  if (band >= 5) return 'var(--chart-4)';
  return 'var(--chart-5)';
}

function getCriterionIcon(criterionType: CriterionType) {
  switch (criterionType) {
    case 'TR':
      return <Award className='h-5 w-5' />;
    case 'CC':
      return <GitBranch className='h-5 w-5' />;
    case 'LR':
      return <BookOpen className='h-5 w-5' />;
    case 'GRA':
      return <Paintbrush className='h-5 w-5' />;
    default:
      return <Check className='h-5 w-5' />;
  }
}

export function ScoreDisplay({
  score,
  isOpen,
  onOpenChange,
  isScoring,
  scoreError,
  taskType = 'task2',
  onRefresh,
}: ScoreDisplayProps) {
  const ScoreIndicator = () => {
    if (isScoring) {
      return (
        <Button
          variant='outline'
          className='gap-2 fixed bottom-4 right-4 shadow-lg'
          disabled
        >
          <div className='flex items-center'>
            <span className='font-mono mr-2'>
              Analyzing {taskType === 'task1' ? 'report' : 'essay'}
            </span>
            <div className='flex space-x-1'>
              <div
                className='w-2 h-2 bg-primary rounded-full animate-bounce'
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className='w-2 h-2 bg-primary rounded-full animate-bounce'
                style={{ animationDelay: '150ms' }}
              ></div>
              <div
                className='w-2 h-2 bg-primary rounded-full animate-bounce'
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
          </div>
        </Button>
      );
    }

    if (score) {
      return (
        <Button
          variant='outline'
          className='gap-2 fixed bottom-4 right-4 shadow-lg hover:shadow-xl transition-all duration-300'
          onClick={() => onOpenChange(true)}
        >
          <span
            className={'font-mono text-lg font-bold'}
            style={{ color: getBandColor(score.overallBand) }}
          >
            {score.overallBand.toFixed(1)}
          </span>
          <ChevronRight className='h-4 w-4' />
        </Button>
      );
    }

    return null;
  };

  return (
    <>
      <ScoreIndicator />

      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent className='w-[400px] sm:w-[540px] p-0 overflow-hidden'>
          <SheetHeader className='p-6 pb-2'>
            <div className='flex items-center justify-between'>
              <SheetTitle>
                IELTS {taskType === 'task1' ? 'Report' : 'Essay'} Score
              </SheetTitle>
              <div className='rounded-full bg-primary h-16 w-16 flex items-center justify-center'>
                <span className={`text-2xl font-bold text-primary-foreground`}>
                  {score?.overallBand.toFixed(1) || '-'}
                </span>
              </div>
            </div>
            <SheetDescription className='text-sm text-muted-foreground'>
              AI scoring is for reference only.
            </SheetDescription>
          </SheetHeader>

          {scoreError ? (
            <div className='p-6 flex flex-col items-center text-center'>
              <AlertCircle className='h-8 w-8 mb-3 text-red-500' />
              <h4 className='text-base font-semibold mb-2'>Error</h4>
              <p className='text-sm text-muted-foreground'>{scoreError}</p>
              {onRefresh && (
                <Button variant='outline' className='mt-4' onClick={onRefresh}>
                  <RefreshCw className='h-4 w-4 mr-2' />
                  Try Again
                </Button>
              )}
            </div>
          ) : !score ? (
            <div className='p-6 flex flex-col items-center text-center h-[300px] justify-center'>
              <Sparkles className='h-10 w-10 mb-4 text-muted-foreground' />
              <h4 className='text-base font-semibold mb-2'>
                No Score Available
              </h4>
              <p className='text-sm text-muted-foreground'>
                Complete your {taskType === 'task1' ? 'report' : 'essay'} and
                click &quot;
                {taskType === 'task1' ? 'Score Report' : 'Score Essay'}&quot; to
                get your IELTS band score
              </p>
            </div>
          ) : (
            <ScrollArea className='h-[calc(100vh-120px)]'>
              <Tabs defaultValue='overview' className='w-full mt-2'>
                <div className='px-6'>
                  <TabsList className='w-full'>
                    <TabsTrigger value='overview' className='flex-1'>
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value='detailed' className='flex-1'>
                      Detailed
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value='overview' className='px-6 pt-4 space-y-4'>
                  <div className='grid grid-cols-1 gap-4'>
                    {(['TR', 'CC', 'LR', 'GRA'] as CriterionType[]).map(
                      (criterion) => {
                        const criterionScore =
                          score[
                            criterion === 'TR'
                              ? 'taskResponse'
                              : criterion === 'CC'
                              ? 'coherenceAndCohesion'
                              : criterion === 'LR'
                              ? 'lexicalResource'
                              : 'grammaticalRangeAndAccuracy'
                          ];

                        return (
                          <Card key={criterion} className='overflow-hidden'>
                            <CardHeader className='p-4 pb-2 flex flex-row items-center justify-between'>
                              <div className='flex items-center'>
                                {getCriterionIcon(criterion)}
                                <CardTitle className='text-base ml-2'>
                                  {getCriterionName(criterion, taskType)}
                                </CardTitle>
                              </div>
                              <Badge
                                variant='outline'
                                className={'text-base font-mono font-bold'}
                                style={{
                                  color: getBandColor(criterionScore.band),
                                  borderColor: getBandColor(
                                    criterionScore.band
                                  ),
                                }}
                              >
                                {criterionScore.band.toFixed(1)}
                              </Badge>
                            </CardHeader>
                            <CardContent className='p-4 pt-0'>
                              <p className='text-xs text-muted-foreground mb-2'>
                                {getCriterionDescription(criterion, taskType)}
                              </p>
                              <Progress
                                value={criterionScore.band * 11.1}
                                className={'h-2'}
                                style={
                                  {
                                    '--custom-progress-color': getProgressColor(
                                      criterionScore.band
                                    ),
                                  } as React.CSSProperties
                                }
                              />
                            </CardContent>
                          </Card>
                        );
                      }
                    )}
                  </div>
                </TabsContent>

                <TabsContent
                  value='detailed'
                  className='px-6 pt-4 pb-6 space-y-4'
                >
                  {(['TR', 'CC', 'LR', 'GRA'] as CriterionType[]).map(
                    (criterion) => {
                      const criterionScore =
                        score[
                          criterion === 'TR'
                            ? 'taskResponse'
                            : criterion === 'CC'
                            ? 'coherenceAndCohesion'
                            : criterion === 'LR'
                            ? 'lexicalResource'
                            : 'grammaticalRangeAndAccuracy'
                        ];

                      return (
                        <Card key={criterion} className='overflow-hidden'>
                          <CardHeader className='p-4 pb-2 flex flex-row items-center justify-between'>
                            <div className='flex items-center'>
                              {getCriterionIcon(criterion)}
                              <CardTitle className='text-base ml-2'>
                                {getCriterionName(criterion, taskType)}
                              </CardTitle>
                            </div>
                            <Badge
                              variant='outline'
                              className={'text-base font-mono font-bold'}
                              style={{
                                color: getBandColor(criterionScore.band),
                                borderColor: getBandColor(criterionScore.band),
                              }}
                            >
                              {criterionScore.band.toFixed(0)}
                            </Badge>
                          </CardHeader>
                          <CardContent className='p-4'>
                            <div className='text-sm space-y-3'>
                              <div>
                                <h4 className='font-semibold mb-1'>
                                  Feedback:
                                </h4>
                                <p>{criterionScore.feedback}</p>
                              </div>

                              {criterionScore.advice && (
                                <div className='mt-2 border-t pt-2'>
                                  <h4 className='font-semibold mb-1 flex items-center'>
                                    <span className='mr-1'>
                                      How to improve:
                                    </span>
                                    <RefreshCw className='h-3 w-3' />
                                  </h4>
                                  <p className='text-sm'>
                                    {criterionScore.advice}
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }
                  )}
                </TabsContent>

                <TabsContent
                  value='advice'
                  className='px-6 pt-4 pb-6 space-y-4'
                >
                  <Card>
                    <CardHeader className='p-4 pb-2'>
                      <CardTitle className='text-base'>
                        Improvement Suggestions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='p-4'>
                      {score.improvementAdvice.length > 0 ? (
                        <ul className='space-y-3'>
                          {score.improvementAdvice.map((advice, index) => (
                            <li key={index} className='flex items-start'>
                              <Check className='h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5' />
                              <span className='text-sm'>{advice}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className='text-sm text-muted-foreground'>
                          No specific improvement suggestions available.
                        </p>
                      )}
                    </CardContent>
                    {onRefresh && (
                      <CardFooter className='px-4 pb-4 pt-0'>
                        <Button
                          variant='outline'
                          className='mt-2'
                          onClick={onRefresh}
                          size='sm'
                        >
                          <RefreshCw className='h-4 w-4 mr-2' />
                          Refresh Score
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                </TabsContent>
              </Tabs>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
